import React from 'react';

import {
	ApiClientManager,
	SimulationOverviewInputDto,
	SimulationOverviewOutputDtoSingleOutputMessage,
} from '@api/consumerloans-public-api';
import { ContactHubHelper, LoggerHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$helpers';
import { UseFetchHookOutput } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { FormatHelper } from '@bcp-channels-sharedcomponents-reactjs/utils';
import { DEFAULT_SIMULATOR_VALUES } from '@constants';
import { useFetch, useGtm, useHideScroll, useModal } from '@hooks';
import {
	useLoadingStore,
	usePTCMetadata,
	useServicesStore,
	useSessionInfo,
	useSimulationInputData,
	useTempStore,
} from '@store';

export const useFetchSimulationOverview = (): UseFetchHookOutput<
	SimulationOverviewInputDto,
	SimulationOverviewOutputDtoSingleOutputMessage
> => {
	const saveServiceResponse = useServicesStore((state) => state.saveServiceResponse);
	const simulationOverview = useServicesStore((state) => state.simulationOverview);
	const creditTypeSelected = useTempStore((state) => state.creditTypeSelected);
	const metadata = usePTCMetadata();
	const applicationContextId = useServicesStore((state) => state.init?.applicationContextId);
	const sessionInfo = useSessionInfo();
	const creditType = sessionInfo?.journeyData.loanType || creditTypeSelected.creditType || 'CL';
	const setIsLoadingSimulationOverview = useLoadingStore((state) => state.setIsLoadingSimulationOverview);
	const changeIsValidSimulation = useTempStore((state) => state.changeIsValidSimulation);
	const saveNewSimulation = useTempStore((state) => state.saveNewSimulation);
	const { show: showUsuryError } = useModal('noValidSimulation');
	const { show: showModal } = useModal('genericError');
	const { disable } = useHideScroll();
	const { show: showAdjustedPaymentTermSimulationModal } = useModal('adjustedPaymentTermSimulationModal');
	const showedAdjustedPaymentTermSimulation = useTempStore((state) => state.showedAdjustedPaymentTermSimulation);
	const { isPrestige } = DEFAULT_SIMULATOR_VALUES;
	const { amount, term, installment } = useSimulationInputData();
	const saveSimulationInputData = useTempStore((state) => state.saveSimulationInputData);
	const isAdjustedSimulation = useTempStore((state) => state.isAdjustedSimulation);
	const setIsAdjustedSimulation = useTempStore((state) => state.setIsAdjustedSimulation);
	const { sendEvent } = useGtm();
	const payload = React.useRef({
		amount: amount,
		term: term,
		installment: installment,
		accountId: 0,
		metadata: {
			application: '',
			languageCode: 'PT',
		},
		creditType: 'CL',
		isPrestige: false,
	});
	return useFetch<SimulationOverviewInputDto, SimulationOverviewOutputDtoSingleOutputMessage>({
		fetchFn: (data: SimulationOverviewInputDto) => {
			const masterAmount = data.amount || amount || payload.current.amount;
			const masterInstallment = data.installment || installment || payload.current.installment;
			const masterTerm = data.term || term || payload.current.term;
			const masterIsPrestige = data.isPrestige || isPrestige || payload.current.isPrestige;
			const masterCreditType = data.creditType || creditType || payload.current.creditType;

			payload.current = {
				...payload.current,
				amount: masterAmount,
				installment: masterInstallment,
				term: masterTerm,
				isPrestige: masterIsPrestige,
				creditType: masterCreditType,
			};
			setIsLoadingSimulationOverview(true);
			if (isAdjustedSimulation) {
				setIsAdjustedSimulation(false);
			}
			return ApiClientManager.call.simulationOverview({
				data: {
					...data,
					isPrestige: masterIsPrestige,
					creditType: masterCreditType,
					amount: masterAmount,
					term: masterTerm,
					metadata,
					applicationContextId,
				},
			});
		},
		// eslint-disable-next-line sonarjs/cognitive-complexity
		onSuccess: (res: SimulationOverviewOutputDtoSingleOutputMessage) => {
			if (res.data) {
				saveServiceResponse({ simulationOverview: res.data || {} });
				if (
					simulationOverview?.requestedAmount &&
					simulationOverview.requestedAmount !== res.data?.requestedAmount
				) {
					sendEvent(
						{
							// cSpell: disable-next-line
							eName: 'editar-montante',
							eLabel: (res.data?.requestedAmount || 0).toString(),
							amount_desired: (res.data?.requestedAmount || 0).toString(),
							total_months: (res.data?.totalDuration || 0).toString(),
							monthly_payment: (res.data?.repaymentPeriods?.length
								? res.data?.repaymentPeriods[0].installement || 0
								: 0
							)?.toString(),
							mtic: (res.data?.totalLoanCost || 0).toString(),
							taeg: (res.data?.effectiveAnnualInterestRate || 0).toString(),
							tan: (res.data?.repaymentPeriods?.length
								? FormatHelper.formatNumber(
										res.data?.repaymentPeriods[0].interestRate || 0,
										'noformat',
										3
								  )
								: 0
							).toString(),
						},
						true
					);
				}
				setIsLoadingSimulationOverview(false);
				saveNewSimulation(true);
				changeIsValidSimulation(true);
				/* cSpell:disable */
				ContactHubHelper.setContextData({
					montante: res.data?.requestedAmount,
					prazo: res.data?.totalDuration,
					prestacaoMensal: res.data.repaymentPeriods?.[0]?.installement?.toString(),
					idSimulacao: res.data.simulationId,
					tan: res.data.repaymentPeriods?.[0].interestRate?.toString() || '',
				});
				/* cSpell:enable */
				// when amount or term request changed from returned simulation values show specific modal
				if (
					res.data.requestedAmount !== payload.current.amount ||
					res.data.totalDuration !== payload.current.term ||
					res.data.repaymentPeriods?.[0].installement !== payload.current.installment
				) {
					const installment = res.data.repaymentPeriods
						? res.data.repaymentPeriods[0].installement || undefined
						: undefined;
					saveSimulationInputData({
						amount: res.data.requestedAmount || payload.current.amount,
						term: res.data.totalDuration || payload.current.term,
						installment: installment,
					});
					//disable();

					if (res.data.adjustedTermMessages && !showedAdjustedPaymentTermSimulation) {
						disable();
						showAdjustedPaymentTermSimulationModal();
					}
					if (res.data.isAdjustedSimulation) {
						setIsAdjustedSimulation(res.data.isAdjustedSimulation);
					}
				}

				// call Sharepoint function to update data in page
				const fn = (window as any).updateDisclaimerSimulation;
				if (typeof fn === 'function') {
					fn(res.data);
				}
			}
		},
		onError: (err: any) => {
			if (err.code === 'InvalidSimulationData') {
				LoggerHelper.log(
					'useFetchSimulationOverview',
					'services > useFetchSimulationOverview > InvalidSimulationData'
				);
				disable();
				showUsuryError();
			} else {
				disable();
				showModal();
			}
			changeIsValidSimulation(false);
			setIsLoadingSimulationOverview(false);
			// call Sharepoint function to update data in page
			const fn = (window as any).hideDisclaimerSimulation;
			if (typeof fn === 'function') {
				fn();
			}
		},
		notShowLoading: true,
	});
};
